/**
 * OriginNumberChip — escolhe o NÚMERO DE ORIGEM por onde a resposta do contato
 * sai (Funcionalidade B). Reusa o visual dos chips do AI Workspace + um popover
 * (portal) com as opções que o operador PODE escolher (autorização server-side).
 * Selecionar persiste o override por contato (vale para a equipe).
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Phone, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getWorkspaceColors } from './styles';

const firstName = (name) => (name || '').trim().split(/\s+/)[0] || '';

const channelHint = (ch) => (ch === 'wa_business' ? 'Business' : 'QR');

export function originLabel(option) {
  if (!option) return '';
  if (option.is_self) return 'Meu número';
  return firstName(option.member_name) || 'Número';
}

const optionTitle = (option) => {
  if (!option) return '';
  const who = option.is_self ? 'seu número' : (option.member_name || 'número');
  return `${originLabel(option)} · ${who} · ${channelHint(option.channel)}`;
};

const OriginNumberChip = ({ options = [], activeOrigin, onSelect, allowSwitch = true }) => {
  const { colors, theme } = useTheme();
  const wc = getWorkspaceColors(colors, theme === 'dark');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ bottom: 0, left: 0 });
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const rect = btnRef.current.getBoundingClientRect();
    // O AI Workspace fica no RODAPÉ → abrir pra CIMA (ancora o fundo do popover
    // logo acima do chip), senão a lista some embaixo da tela.
    setPos({ bottom: window.innerHeight - rect.top + 6, left: rect.left });
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (!activeOrigin || options.length === 0) return null;
  const canOpen = allowSwitch && options.length > 1;

  const activeKey = activeOrigin.member_id;
  const styles = {
    button: {
      padding: '6px 12px', borderRadius: 18, border: 'none',
      backgroundColor: wc.chipBg || wc.bgTertiary, color: wc.textPrimary,
      cursor: canOpen ? 'pointer' : 'default', display: 'flex', alignItems: 'center',
      gap: 6, fontSize: 12, fontWeight: 600,
    },
    pop: {
      position: 'fixed', bottom: pos.bottom, left: pos.left, minWidth: 220,
      maxHeight: '42vh', overflowY: 'auto',
      backgroundColor: wc.bgPrimary, border: `1px solid ${wc.borderColor}`,
      borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.3)', zIndex: 10000,
      padding: 6, display: 'flex', flexDirection: 'column', gap: 2,
    },
    item: (isActive) => ({
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
      borderRadius: 8, cursor: 'pointer', fontSize: 13,
      color: wc.textPrimary,
      backgroundColor: isActive ? (wc.chipBg || wc.bgSecondary) : 'transparent',
    }),
    itemSub: { fontSize: 11, color: wc.textSecondary, marginLeft: 'auto' },
  };

  const choose = (opt) => {
    setOpen(false);
    // is_default → limpa o override (volta ao número da thread ativa).
    onSelect?.(opt.is_default ? null : opt);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        style={styles.button}
        title={canOpen
          ? `Trocar o número de origem do envio — atual: ${optionTitle(activeOrigin)}`
          : `Enviar por: ${optionTitle(activeOrigin)}`}
        onClick={() => canOpen && setOpen((v) => !v)}
      >
        <Phone size={14} />
        <span style={{ opacity: 0.7 }}>Origem:</span>
        {originLabel(activeOrigin)}
        {canOpen && (open ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </button>

      {open && createPortal(
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9999 }}
            onClick={() => setOpen(false)}
          />
          <div style={styles.pop}>
            {options.map((opt) => (
              <div
                key={opt.key || opt.member_id}
                style={styles.item(opt.member_id === activeKey)}
                onClick={() => choose(opt)}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = wc.chipBg || wc.bgSecondary; }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = opt.member_id === activeKey
                    ? (wc.chipBg || wc.bgSecondary) : 'transparent';
                }}
              >
                {opt.member_id === activeKey
                  ? <Check size={14} color={wc.accentPrimary} />
                  : <span style={{ width: 14 }} />}
                <span>
                  {originLabel(opt)}
                  {opt.is_default && (
                    <span style={{ color: wc.textSecondary, fontWeight: 400 }}> (padrão)</span>
                  )}
                </span>
                <span style={styles.itemSub}>
                  {opt.is_self ? 'você' : (firstName(opt.member_name) || '')} · {channelHint(opt.channel)}
                </span>
              </div>
            ))}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

export default OriginNumberChip;
